/**
 * MigrateDropdown.tsx — Themed "Migrate" dropdown button.
 *
 * Replaces the Bootstrap DropdownButton in AppHeader actions.
 *
 * Usage:
 *   <MigrateDropdown onSelect={(tool) => this.setState({ migrationTool: tool }, this.handleMigrateDMData)} />
 */

import React, { useState, useRef, useEffect } from 'react';
import { T } from '../theme/theme';

interface MigrateOption {
  key: string;
  label: string;
  sublabel: string;
  icon: string;
  badgeBg: string;
  badgeColor: string;
  badgeBorder: string;
}

const OPTIONS: MigrateOption[] = [
  {
    key: 'MIGRATION_INFA_LEGACY_TO_IDMC',
    label: 'IDMC',
    sublabel: 'Informatica Intelligent Cloud',
    icon: '☁️',
    badgeBg: T.brandLight,
    badgeColor: T.brand,
    badgeBorder: T.brandMid,
  },
  {
    key: 'MIGRATION_INFA_LEGACY_TO_RELTIO',
    label: 'Reltio',
    sublabel: 'Reltio Cloud MDM',
    icon: '🔄',
    badgeBg: T.purpleBg,
    badgeColor: T.purple,
    badgeBorder: T.purpleBorder,
  },
];

interface Props {
  onSelect: (tool: string) => void;
  disabled?: boolean;
}

const MigrateDropdown: React.FC<Props> = ({ onSelect, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (key: string) => {
    setOpen(false);
    onSelect(key);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>

      {/* Trigger button */}
      <button
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '5px 13px',
          borderRadius: 8,
          fontSize: 11,
          fontWeight: 700,
          fontFamily: "'Sora', system-ui, sans-serif",
          cursor: disabled ? 'not-allowed' : 'pointer',
          border: `1px solid ${T.brandMid}`,
          background: open ? T.brandLight : T.bgCard,
          color: T.brand,
          opacity: disabled ? 0.5 : 1,
          transition: 'background .13s, border-color .13s',
          whiteSpace: 'nowrap',
          lineHeight: 1,
        }}
        onMouseEnter={(e) => {
          if (!disabled) (e.currentTarget as HTMLButtonElement).style.background = T.brandLight;
        }}
        onMouseLeave={(e) => {
          if (!open) (e.currentTarget as HTMLButtonElement).style.background = T.bgCard;
        }}
      >
        {/* Export/arrow icon */}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
          <path d="M6 1v7M3 5l3 3 3-3" stroke={T.brand} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 10h8" stroke={T.brand} strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        Migrate
        {/* Chevron */}
        <svg
          width="10" height="10" viewBox="0 0 10 10" fill="none"
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}
        >
          <path d="M2 3.5l3 3 3-3" stroke={T.brand} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 9999,
            background: T.bgCard,
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            boxShadow: '0 8px 32px rgba(10,20,40,.13)',
            minWidth: 220,
            overflow: 'hidden',
            animation: 'gwDropIn .14s ease',
          }}
        >
          {/* Panel header */}
          <div
            style={{
              padding: '9px 14px 8px',
              borderBottom: `1px solid ${T.border}`,
              background: T.bgSidebar,
            }}
          >
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: '.16em',
                textTransform: 'uppercase' as const,
                color: T.textMuted,
              }}
            >
              Select Target Platform
            </span>
          </div>

          {/* Options */}
          {OPTIONS.map((opt) => {
            const isHovered = hovered === opt.key;
            return (
              <div
                key={opt.key}
                onClick={() => handleSelect(opt.key)}
                onMouseEnter={() => setHovered(opt.key)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '11px 14px',
                  cursor: 'pointer',
                  background: isHovered ? T.brandLight : 'transparent',
                  borderLeft: isHovered ? `3px solid ${T.brand}` : '3px solid transparent',
                  transition: 'background .12s, border-color .12s',
                }}
              >
                {/* Icon */}
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: isHovered ? T.brand : T.bgSidebar,
                    border: `1px solid ${isHovered ? T.brand : T.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 15,
                    flexShrink: 0,
                    transition: 'background .12s, border-color .12s',
                  }}
                >
                  {opt.icon}
                </div>

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: isHovered ? T.brand : T.textPrimary,
                      fontFamily: "'Sora', system-ui, sans-serif",
                      transition: 'color .12s',
                    }}
                  >
                    {opt.label}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: T.textMuted,
                      fontWeight: 300,
                      marginTop: 1,
                      fontFamily: "'Sora', system-ui, sans-serif",
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {opt.sublabel}
                  </div>
                </div>

                {/* Badge */}
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 8,
                    fontWeight: 700,
                    padding: '2px 7px',
                    borderRadius: 8,
                    letterSpacing: '.05em',
                    background: opt.badgeBg,
                    color: opt.badgeColor,
                    border: `1px solid ${opt.badgeBorder}`,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {opt.label.toUpperCase()}
                </span>
              </div>
            );
          })}

          {/* Panel footer note */}
          <div
            style={{
              padding: '8px 14px',
              borderTop: `1px solid ${T.border}`,
              background: T.bgSidebar,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <circle cx="5" cy="5" r="4" stroke={T.textMuted} strokeWidth="1"/>
              <path d="M5 4v3M5 3h.01" stroke={T.textMuted} strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9,
                color: T.textMuted,
                fontWeight: 400,
                letterSpacing: '.04em',
              }}
            >
              Config will be validated before migration
            </span>
          </div>
        </div>
      )}

      {/* Keyframe injected inline once */}
      <style>{`@keyframes gwDropIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
};

export default MigrateDropdown;